import React, { useContext, useEffect } from 'react'
import { Container, Row, Col, Button, Form, FormCheck } from 'react-bootstrap'
import { itemLabels } from '@srca-b2b/kp5-ui-common/lib/agreements'
import AgreementFormGroup from '../../components/AgreementFormGroup/AgreementFormGroup'
import { Formik, useFormikContext } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'next-i18next'
import { EnrollmentDispatch, EnrollmentContext } from '../../context/enrollmentContext'

import classNames from 'classnames'
import styles from './enrollment.module.scss'
import {
  scpePrivacyPolicyLegalTypes,
  isAllRequiredFieldsValid
} from '@srca-b2b/kp5-ui-common/utils/helpers'
import GdprConsentElements from './GdprConsentElements'
import MarketingConsentForm from '../../components/MarketingConsentForm/MarketingConsentForm'

const EnrollmentStep4 = () => {
  const dispatch = useContext(EnrollmentDispatch)
  const state = useContext(EnrollmentContext)
  const { isSubmitting, registrationData } = state
  const { t } = useTranslation('common')

  console.log('EnrollmentStep4 state:', state)

  // NOTE: Agreements are in 2 sections. Top section contains these agreements where
  // acceptance is automatic/implied. 2nd section requires explicit check boxes.
  let topSectionAgreements = [
    'registration_terms_use',
    'registration_privacy_policy',
    'privacy_policy_usa',
    'privacy_policy_eu',
    'privacy_policy_br',
    'privacy_policy_vn',
    'kcs_product_terms_conditions',
    'kcs_data_processing_agreement',
    // Turkey (privacy_policy_tk) is a one-off. It shows registration_terms_use and
    // privacy_policy_tk as normal checkboxes in the list. So we must remove them from
    // topSectionAgreements. The order of gdpr vs non-gdpr is also very different than
    // the others.
    'privacy_policy_tk' // KP5-3152 - Removal of cross-over data transfer for Turkiye but top section must still include the privacy_policy_tk
  ]

  let agreementsToAccept = [...state.gdprLegalAgreements, ...state.nonGdprLegalAgreements]
  // If SCPE Privacy Policy is present, add path from API.
  const scpeLegalTypes = scpePrivacyPolicyLegalTypes()
  const scpeType = agreementsToAccept.find((legalType) => scpeLegalTypes.includes(legalType))
  if (scpeType) {
    itemLabels[scpeType].externalHref = state.scpePpPath
  }

  // For KG and SCB case, see KP5-1396 "Backward Compatibility" under Non-functional Requirements
  let formAgreements = ['kg_terms_agreement', 'msp_terms_agreement', ...scpeLegalTypes]
  if (state.registrationData.country === 'KR') {
    if (
      state.productParam === 'kg' ||
      state.nonGdprLegalAgreements.includes('kg_terms_agreement')
    ) {
      topSectionAgreements.push('kg_terms_agreement')
      formAgreements = formAgreements.filter((item) => item != 'kg_terms_agreement')
    } else if (
      state.productParam === 'kscp' ||
      state.nonGdprLegalAgreements.includes('privacy_policy_ko_scpe')
    ) {
      topSectionAgreements.push('privacy_policy_ko_scpe')
      formAgreements = formAgreements.filter((item) => item != 'privacy_policy_ko_scpe')
    } else if (state.productParam === 'msp') {
      topSectionAgreements.push('msp_terms_agreement')
      formAgreements = formAgreements.filter((item) => item != 'msp_terms_agreement')
    }
  }

  // If KG T&C is present, update path based on region
  if (agreementsToAccept.includes('kg_terms_agreement')) {
    // exclude kcs_product_terms_conditions for KG service T&C (KP5-2772)
    formAgreements.push('kcs_product_terms_conditions')

    let kgSuffix = state.isKgTcEU ? '_eu' : '_non_eu'
    itemLabels[`kg_terms_agreement`].externalHref =
      itemLabels[`kg_terms_agreement${kgSuffix}`].externalHref
  }
  let nextButtonText = t('Agree')
  const regionsWithNextButtonOverride = [
    'privacy_policy_eu',
    'privacy_policy_br',
    'privacy_policy_tk'
  ]
  const overrideNextButton = regionsWithNextButtonOverride.find((legalType) =>
    agreementsToAccept.includes(legalType)
  )
  if (overrideNextButton) {
    nextButtonText = t('Continue')
  }

  // Define legal types that requires 'Agree all' checkbox
  const ppWithAgreeAll = []
  const showAgreeAll = ppWithAgreeAll.some((legalType) => agreementsToAccept.includes(legalType))

  let agreementSchema = {
    receiveEmails: Yup.bool().optional().oneOf([true, false])
  }
  // IMPORTANT: We must define initialValues for every checkbox in order to use
  // booleans. If we miss one, it will default to ["on"] ["off"] array format which
  // will not work with booleans.
  const initialValues = {
    receiveEmails: false
  }
  if (showAgreeAll) {
    initialValues.agreeAll = false
  }
  if (agreementsToAccept.length > 0) {
    agreementsToAccept.forEach((type) => {
      if (topSectionAgreements.includes(type)) {
        initialValues[type] = true
      } else {
        initialValues[type] = false
        agreementSchema[type] = Yup.bool().required().oneOf([true], t('Required'))
      }
    })
  }

  agreementSchema = Yup.object().shape(agreementSchema)

  const onBackClick = (formValues) => {
    dispatch({ type: 'GOTO_STEP_3', payload: formValues })
  }

  const onSubmitRegistration = (formValues) => {
    dispatch({ type: 'SUBMIT_REGISTRATION_START', payload: formValues })
  }

  const CheckAgreements = () => {
    const { values, setFieldValue } = useFormikContext()
    useEffect(() => {
      let agreeToAll = true
      for (const key in values) {
        if (key !== 'agreeAll' && !values[key]) {
          agreeToAll = false
          break
        }
      }
      setFieldValue('agreeAll', agreeToAll)
    }, [values])
    return null
  }

  return (
    <Container id="enrollment-step4" className={styles.enrollmentStep4}>
      <h4 className={classNames('text-center', styles.headerTitle)}>
        {t('Samsung_Knox_agreements')}
      </h4>
      <Formik
        validationSchema={agreementSchema}
        onSubmit={onSubmitRegistration}
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={initialValues}
      >
        {({ handleSubmit, handleChange, handleBlur, errors, values, setFieldValue }) => {
          const agreeAllHandleChange = (e) => {
            // So far, this is only used by Turkey.
            const val = e.target.checked
            agreementsToAccept
              .filter((legalType) => !topSectionAgreements.includes(legalType))
              .forEach((legalType) => {
                setFieldValue(legalType, val)
              })
            setFieldValue('receiveEmails', val)
            setFieldValue('agreeAll', val)
          }

          return (
            <Form onSubmit={handleSubmit}>
              <div className={styles.formBodyContainer}>
                <div className={styles.formBody}>
                  <FormError errors={errors} />
                  <div className="text-center">
                    <GdprConsentElements
                      agreementsToAccept={agreementsToAccept.filter(
                        (legalType) => !formAgreements.includes(legalType)
                      )}
                      formAgreements={formAgreements}
                    />
                  </div>
                  <div>
                    {agreementsToAccept
                      .filter((legalType) => !topSectionAgreements.includes(legalType))
                      .map((legalType) => (
                        <Row key={legalType} className={styles.cbContainer}>
                          {legalType === 'privacy_policy_eu' ? (
                            <Col className="mx-auto">
                              <p className={styles.labelStyle}>
                                {itemLabels[legalType].introText}(
                                {itemLabels[legalType].closingText}).
                              </p>
                            </Col>
                          ) : (
                            <AgreementFormGroup
                              errors={errors}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              name={legalType}
                              id={legalType}
                              label={itemLabels[legalType]}
                            />
                          )}
                        </Row>
                      ))}
                    <Row className={styles.cbContainer}>
                      {registrationData.country === 'KR' ? (
                        <MarketingConsentForm
                          errors={errors}
                          handleSubmit={handleBlur}
                          handleChange={handleChange}
                          name={'receiveEmails'}
                          id={
                            'ga-kp-getstarted-signin-companyinfo-agreement-infoandagreement-checkbox'
                          } //TODO: to change once click IDs have been provided
                          country={registrationData.country}
                        />
                      ) : (
                        <AgreementFormGroup
                          errors={errors}
                          handleBlur={handleBlur}
                          handleChange={handleChange}
                          name={'receiveEmails'}
                          id="ga-kp-getstarted-signin-companyinfo-agreement-infoandagreement-checkbox"
                          label={itemLabels.receiveEmails}
                          country={registrationData.country}
                        />
                      )}
                    </Row>
                    {showAgreeAll && (
                      <Row className={styles.cbContainer}>
                        <Form.Group as={Col} controlId={'agreeAll'} className="mx-auto">
                          <FormCheck custom inline>
                            <FormCheck.Input
                              onChange={agreeAllHandleChange}
                              onBlur={handleBlur}
                              type={'checkbox'}
                              name={'agreeAll'}
                              id={'agreeAll'}
                              checked={values.agreeAll}
                            />
                            <FormCheck.Label htmlFor={'agreeAll'} className="font-weight-normal">
                              {t('I_agree_to_all_optional')}
                            </FormCheck.Label>
                          </FormCheck>
                        </Form.Group>
                      </Row>
                    )}
                  </div>
                </div>
              </div>
              <Row className={styles.enrollmentButtonWrapper}>
                <Col md="auto">
                  <Button
                    id="ga-registration-knoxagreement-back"
                    onClick={() => {
                      onBackClick(values)
                    }}
                    disabled={isSubmitting}
                    className="btn-registration-next btn-outline-primary arrow-left"
                  >
                    {t('Back')}
                    <span className={styles.changePageTitle}>: {t('Company_Info')}</span>
                  </Button>
                </Col>
                <Col md="auto">
                  <Button
                    id="ga-registration-knoxagreement-submit"
                    className="arrow-right"
                    type="submit"
                    disabled={isSubmitting || isAllRequiredFieldsValid(agreementSchema, values)}
                  >
                    {nextButtonText}
                  </Button>
                </Col>
              </Row>
              <CheckAgreements />
            </Form>
          )
        }}
      </Formik>
    </Container>
  )
}

const FormError = () => {
  return <></>
}

export default EnrollmentStep4
